/* eslint-disable max-len */
import { useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, ExternalLink, FileText, Home, Wrench } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CopyableVin } from '@/components/CopyableVin';
import { CustomerContactReveal } from '@/components/CustomerContactReveal';
import { CUSTOMER_STOP_LABEL } from '@/lib/manualStops/locationKey';
import { getManualStopReasonLabel } from '@/lib/validation/manualStopValidation';
import {
  STATUS_META,
  STATUS_ORDER,
  firstRowPerHousehold,
  type UpdateHistoryEntry
} from '../../utils/buildUpdateHistory';
import { addressPartsFromRecall, formatStreetAddress } from '../../utils/addressFormat';
import { formatVehicleLine } from '../../utils/formatVehicle';
import type { RouteRecallDetail } from '@/types/routeTracking';
import type { ServiceEventCompletion } from '@/types/serviceEvents';

export interface TechDayReport {
  technicianId: string;
  technicianName: string;
  routeDate: string; // yyyy-mm-dd
  // All entries resolved on this day for this tech (across route sequences).
  entries: UpdateHistoryEntry[];
  // Totals across all of the tech's routes — drives the "8/8 complete" chip.
  totalRecalls: number;
  completedCount: number;
  // KPI strip inputs (only rendered when at least one event has hours_worked).
  activeMinutes: number;
  earliestStart?: string;
  latestEnd?: string;
}

interface TechDayCardProps {
  report: TechDayReport;
  // Map keyed by assignmentId — drives the "View work details" affordance and
  // the click-to-open-drawer gating (preserves graceful degradation).
  serviceEventsMap: Map<string, ServiceEventCompletion> | undefined;
  onOpenWorkSummary: (recall: RouteRecallDetail) => void;
}

function formatMinutesShort(min: number): string {
  if (!Number.isFinite(min) || min <= 0) return '—';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function formatTime(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return '—';
  }
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function TechDayCard({ report, serviceEventsMap, onOpenWorkSummary }: TechDayCardProps) {
  // Card collapses by default so a multi-tech day scans as a stack of summary
  // rows — users expand only the tech they care about. Progressive disclosure
  // (Nielsen heuristic) trades one click for far less vertical scroll on busy days.
  const [expanded, setExpanded] = useState(false);

  // Per-status groups: when the card is expanded, Completed reads first by
  // default since that's the dominant outcome on a typical day. Other groups
  // (Released, Incomplete, etc.) collapse so they don't drown the scan.
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set(['completed']));
  const toggleGroup = (status: string) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  // Bucket entries by status.
  const buckets = new Map<UpdateHistoryEntry['status'], UpdateHistoryEntry[]>();
  STATUS_ORDER.forEach(s => buckets.set(s, []));
  report.entries.forEach(e => buckets.get(e.status)?.push(e));

  const completePct =
    report.totalRecalls > 0 ? Math.round((report.completedCount / report.totalRecalls) * 100) : 0;

  // KPI strip is opt-in: shown only when iOS-captured work has actually landed
  // (active time > 0). Tenants with no iOS adoption see a clean card without
  // half-empty metrics.
  const showKpiStrip = report.activeMinutes > 0;

  const dailySummaryHref = `/routing/summary?technicianId=${encodeURIComponent(report.technicianId)}&date=${encodeURIComponent(report.routeDate)}`;

  return (
    <Card className='mb-3 overflow-hidden'>
      {/* Header — entire row is the collapse toggle. Daily summary link inside
          uses stopPropagation so clicking it doesn't accidentally toggle. */}
      <div
        role='button'
        tabIndex={0}
        aria-expanded={expanded}
        aria-controls={`tech-day-body-${report.technicianId}`}
        onClick={() => setExpanded(v => !v)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(v => !v);
          }
        }}
        className='flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-4 py-3 transition-colors hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-none dark:border-gray-800 dark:bg-gray-900/40 dark:hover:bg-gray-900/60 cursor-pointer'
      >
        <div className='flex min-w-0 items-center gap-3'>
          <div className='flex h-5 w-5 shrink-0 items-center justify-center text-gray-500'>
            {expanded ? <ChevronDown className='h-4 w-4' /> : <ChevronRight className='h-4 w-4' />}
          </div>
          <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary'>
            {initials(report.technicianName) || '?'}
          </div>
          <div className='min-w-0'>
            <div className='truncate text-sm font-semibold text-gray-900 dark:text-gray-100'>
              {report.technicianName}
            </div>
            {/* Secondary line: completion stats + per-status count chips so a
                dispatcher can see the full breakdown (how many completed vs
                released vs sold etc.) at a glance, even with the card collapsed.
                Only non-zero statuses render. Tooltip on each chip carries the
                full status label for accessibility. */}
            <div
              className='flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500'
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              <span>
                {report.completedCount}/{report.totalRecalls} complete · {completePct}%
              </span>
              {STATUS_ORDER.map(status => {
                const count = buckets.get(status)?.length ?? 0;
                if (count === 0) return null;
                const meta = STATUS_META[status];
                return (
                  <span
                    key={status}
                    className='inline-flex items-center gap-1'
                    title={`${count} ${meta.label}`}
                  >
                    <span
                      aria-hidden='true'
                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${meta.dotClass}`}
                    />
                    <span className={`font-semibold ${meta.colorClass}`}>{count}</span>
                    <span className='sr-only'>{meta.label}</span>
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right cluster: two-line stack — KPIs on top, Daily Summary button below.
            Two-line separates passive info (metrics) from active affordance (button)
            and keeps a deterministic right-edge column across all tech cards so
            multi-tech days scan cleanly.

            Label-value pair pattern: tiny uppercase muted label + larger value.
            The label is "learn once, then ignore" — it teaches new users what each
            number means without competing for attention with the number itself.
            Same treatment as form-field metadata in Linear/Stripe/Datadog. */}
        <div className='flex shrink-0 flex-col items-end gap-2'>
          {showKpiStrip && (
            <div
              className='flex items-center gap-x-3'
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              <span
                className='inline-flex items-baseline gap-1.5'
                title='Active time the technician spent working (sum of recall durations)'
              >
                <span
                  className='text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500'
                  style={{ letterSpacing: '0.06em' }}
                >
                  Active
                </span>
                <span className='text-xs text-gray-700 dark:text-gray-200'>
                  {formatMinutesShort(report.activeMinutes)}
                </span>
              </span>
              <span
                className='inline-flex items-baseline gap-1.5'
                title='Time on site (earliest start → latest finish)'
              >
                <span
                  className='text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500'
                  style={{ letterSpacing: '0.06em' }}
                >
                  On-site
                </span>
                <span className='text-xs text-gray-700 dark:text-gray-200'>
                  {formatTime(report.earliestStart)} – {formatTime(report.latestEnd)}
                </span>
              </span>
            </div>
          )}
          <Link
            to={dailySummaryHref}
            onClick={e => e.stopPropagation()}
            className='inline-flex shrink-0 items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium transition-colors hover:bg-accent hover:text-accent-foreground'
            title='Open printable daily route summary for this technician'
          >
            <ExternalLink className='h-3 w-3' />
            Open daily summary
          </Link>
        </div>
      </div>

      {/* Body: per-status groups — only rendered when the card is expanded. */}
      {expanded && (
        <CardContent id={`tech-day-body-${report.technicianId}`} className='space-y-2 px-3 py-3'>
          {report.entries.length === 0 && (
            <p className='px-2 py-3 text-xs text-gray-500'>(no resolved items yet today)</p>
          )}
          {STATUS_ORDER.map(status => {
            const items = buckets.get(status) ?? [];
            if (items.length === 0) return null;
            const meta = STATUS_META[status];
            const isOpen = openGroups.has(status);
            const contactRows = firstRowPerHousehold(items);
            return (
              <div key={status} className='rounded-md border border-gray-100 dark:border-gray-800'>
                <button
                  type='button'
                  onClick={() => toggleGroup(status)}
                  className='flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium hover:bg-muted/40'
                  aria-expanded={isOpen}
                >
                  <span className='flex items-center gap-2'>
                    {isOpen ? (
                      <ChevronDown className='h-4 w-4' />
                    ) : (
                      <ChevronRight className='h-4 w-4' />
                    )}
                    <span className={meta.colorClass}>{meta.label}</span>
                    <span
                      className='text-xs text-gray-500'
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      ({items.length})
                    </span>
                  </span>
                </button>
                {isOpen && (
                  <div className='divide-y divide-gray-100 dark:divide-gray-800'>
                    {items.map(entry => (
                      <RecallEntryRow
                        key={`${entry.assignmentId}-${entry.updatedAt}`}
                        entry={entry}
                        hasWorkDetails={
                          entry.status === 'completed' &&
                          !!entry.assignmentId &&
                          !!serviceEventsMap?.has(entry.assignmentId)
                        }
                        onOpen={() => onOpenWorkSummary(entry.recall)}
                        showContact={contactRows.has(entry)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      )}
    </Card>
  );
}

interface RecallEntryRowProps {
  entry: UpdateHistoryEntry;
  hasWorkDetails: boolean;
  onOpen: () => void;
  /** First row of a customer household in its group: offer the contact reveal here */
  showContact: boolean;
}

function RecallEntryRow({ entry, hasWorkDetails, onOpen, showContact }: RecallEntryRowProps) {
  const isManual = entry.recall.stopType === 'manual';
  const isCustomer = entry.recall.stopKind === 'customer';
  const campaignLabel =
    isManual && entry.recall.manualStopReason
      ? getManualStopReasonLabel(entry.recall.manualStopReason)
      : entry.recall.campaignNumber || '—';

  // Full street address — same builder used by the Daily Summary + printable surfaces
  // so the same dealer always reads identically across the app.
  const streetAddress = formatStreetAddress(addressPartsFromRecall(entry.recall));
  const vehicleLine = formatVehicleLine(entry.recall);
  // 3px colored left border per row — subtle reinforcement of status that
  // survives scroll/screenshot context loss. Color always pairs with the group
  // header label so this is supplementary, not the only signal.
  const statusBorder = STATUS_META[entry.status].borderClass;

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <div
      className={[
        'flex items-start gap-3 border-l-[3px] px-3 py-2',
        statusBorder,
        hasWorkDetails
          ? 'cursor-pointer transition-colors hover:bg-muted/50 focus-visible:bg-muted/60 focus-visible:outline-none'
          : ''
      ].join(' ')}
      role={hasWorkDetails ? 'button' : undefined}
      tabIndex={hasWorkDetails ? 0 : undefined}
      onClick={hasWorkDetails ? onOpen : undefined}
      onKeyDown={hasWorkDetails ? handleKeyDown : undefined}
      aria-label={hasWorkDetails ? 'View work summary captured at completion' : undefined}
    >
      <div className='flex shrink-0 items-center pt-0.5'>
        {isManual ? (
          <Wrench className='h-3.5 w-3.5 text-orange-600 dark:text-orange-400' />
        ) : (
          <FileText className='h-3.5 w-3.5 text-blue-600 dark:text-blue-400' />
        )}
      </div>
      {/* Left column: identifiers, then vehicle, then (when applicable) the
          View-work-details link, then any release reason. Compact stack so the
          row doesn't grow taller than the dealer info on the right. */}
      <div className='min-w-0 flex-1'>
        <div className='flex flex-wrap items-center gap-x-2 gap-y-0.5'>
          <Badge
            variant='outline'
            className={[
              'text-[10px] font-medium',
              isManual
                ? 'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-900/40 dark:bg-orange-900/20 dark:text-orange-300'
                : 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-300'
            ].join(' ')}
          >
            {campaignLabel}
          </Badge>
          <CopyableVin vin={entry.recall.vin} className='text-xs' />
        </div>
        {vehicleLine && (
          <p className='mt-0.5 text-[11px] text-gray-600 dark:text-gray-400'>{vehicleLine}</p>
        )}
        {hasWorkDetails && (
          <p className='mt-0.5 text-xs font-medium text-green-700 dark:text-green-400'>
            View work details ↗
          </p>
        )}
        {entry.recall.dispatcherNote && (
          <p className='mt-0.5 text-xs text-gray-500 dark:text-gray-400 whitespace-pre-line'>
            Note for technician: {entry.recall.dispatcherNote}
          </p>
        )}
        {entry.otherReason && (
          <p className='mt-0.5 text-xs italic text-gray-500'>Reason: {entry.otherReason}</p>
        )}
        {showContact && (
          // The row opens the work summary; keep the reveal's clicks to itself.
          <div
            role='presentation'
            className='mt-1'
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
          >
            <CustomerContactReveal contactId={entry.recall.contactId} context='route_details' />
          </div>
        )}
      </div>
      {/* Right column: dealer name + address only — keeps the scan column for
          dealer info clean and consistent across rows. */}
      <div className='flex w-[200px] shrink-0 flex-col items-end gap-0.5 text-right'>
        <div className='flex items-center gap-1 truncate text-xs font-medium text-gray-800 dark:text-gray-200'>
          {isCustomer && <Home className='h-3 w-3 shrink-0 text-muted-foreground' aria-hidden />}
          {isCustomer ? CUSTOMER_STOP_LABEL : entry.recall.dealerName || '—'}
        </div>
        {streetAddress && (
          <div className='text-[11px] leading-tight text-gray-500 dark:text-gray-400'>
            {streetAddress}
          </div>
        )}
      </div>
    </div>
  );
}
